import type { Item } from '@/api/gen/_model/item';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import {
  EMPTY_MATCH_PLACEHOLDERS,
  resolveItemLabel,
} from '@/shared/quiz/matchPairDisplay';

import { type AnswerPageBlockData, type QuestionPageBlockData } from '../type';

// Worksheet 은 훅을 쓸 수 없는 pure 빌더라 빈 placeholder 를 쓴다.
// text → richText 평문 순으로 해석하며, raw HTML/id 는 노출하지 않는다.
const getMatchItemLabel = (item?: Item) =>
  resolveItemLabel(item, EMPTY_MATCH_PLACEHOLDERS);
export const getQuestion = (quiz: QuizSetData) => {
  switch (quiz.type) {
    case 'multiple':
      return quiz.multipleData?.question as string;
    case 'ox':
      return quiz.oxData?.question as string;
    case 'subjective':
      return quiz.subjectiveData?.question as string;
    case 'essay':
      return quiz.essayData?.question as string;
    case 'match':
      return quiz.matchData?.question as string;
    case 'order':
      return quiz.orderData?.question as string;
    default:
      return '';
  }
};

export const getQuestionRichText = (quiz: QuizSetData) => {
  let richText: string | undefined;

  switch (quiz.type) {
    case 'multiple':
      richText = quiz.multipleData?.questionRichText as string;
      break;
    case 'ox':
      richText = quiz.oxData?.questionRichText as string;
      break;
    case 'subjective':
      richText = quiz.subjectiveData?.questionRichText as string;
      break;
    case 'essay':
      richText = quiz.essayData?.questionRichText as string;
      break;
    case 'match':
      richText = quiz.matchData?.questionRichText as string;
      break;
    case 'order':
      richText = quiz.orderData?.questionRichText as string;
      break;
    default:
      richText = undefined;
      break;
  }

  if (!richText || richText.trim() === '') {
    return undefined;
  }

  return richText;
};

export const getQuizType = (quiz: QuizSetData) => {
  return quiz.type as
    | 'multiple'
    | 'ox'
    | 'subjective'
    | 'essay'
    | 'match'
    | 'order';
};

export const quizDataToQuestionPageBlock = (quizzes: QuizSetData[]) => {
  const blocks: QuestionPageBlockData[] = [];

  quizzes.forEach((quiz, index) => {
    const question = getQuestion(quiz);
    const questionRichText = getQuestionRichText(quiz);
    const orderItems = quiz.type === 'order' ? quiz.orderData?.items ?? [] : [];
    blocks.push({
      type: 'question',
      data: {
        question,
        questionRichText,
        questionImageUrl: quiz.imageUrl,
        questionNumber: index + 1,
        questionType: getQuizType(quiz),
        ...(quiz.type === 'order' ? { orderItemCount: orderItems.length } : {}),
      },
    });
    switch (quiz.type) {
      case 'multiple':
        quiz.multipleData?.choiceListV2?.forEach((choice, choiceIndex) => {
          blocks.push({
            type: 'multipleChoice',
            data: {
              choice,
              choiceNumber: choiceIndex + 1,
            },
          });
        });
        break;
      case 'ox':
        blocks.push({
          type: 'oxChoice',
          data: 'o',
        });
        blocks.push({
          type: 'oxChoice',
          data: 'x',
        });
        break;
      case 'subjective':
        blocks.push({
          type: 'subjective',
          data: {},
        });
        if (quiz.subjectiveData?.ignoreSpaces === false) {
          blocks.push({ type: 'spacingRule', data: {} });
        }
        break;
      case 'essay':
        blocks.push({
          type: 'essay',
          data: {},
        });
        break;
      case 'match':
        blocks.push({
          type: 'matchChoice',
          data: {
            leftItems: quiz.matchData?.leftItems ?? [],
            rightItems: quiz.matchData?.rightItems ?? [],
          },
        });
        break;
      case 'order':
        orderItems.forEach(item => {
          blocks.push({
            type: 'orderChoice',
            data: { item },
          });
        });
        break;
    }
    if (quiz.hint?.type === 'text' && quiz.hint.value) {
      blocks.push({
        type: 'hint',
        data: { value: quiz.hint.value },
      });
    }
  });

  return blocks;
};

export const quizDataToAnswerPageBlock = (quizzes: QuizSetData[]) => {
  const blocks: AnswerPageBlockData[] = [];

  quizzes.forEach((quiz, index) => {
    const question = getQuestion(quiz);
    const questionRichText = getQuestionRichText(quiz);
    blocks.push({
      type: 'question',
      data: {
        question,
        questionRichText,
        questionNumber: index + 1,
        questionType: getQuizType(quiz),
        ...(quiz.type === 'match' ? { questionImageUrl: quiz.imageUrl } : {}),
      },
    });
    switch (quiz.type) {
      case 'multiple':
        blocks.push({
          type: 'multipleChoiceAnswer',
          data: {
            answerNumberList: quiz.multipleData?.answerList ?? [],
          },
        });
        break;
      case 'ox':
        blocks.push({
          type: 'oxChoiceAnswer',
          data: quiz.oxData?.answer as 'o' | 'x',
        });
        break;
      case 'subjective':
        blocks.push({
          type: 'subjectiveAnswer',
          data: {
            answerList: quiz.subjectiveData?.answerList ?? [],
          },
        });
        break;
      case 'essay':
        blocks.push({
          type: 'essayAnswer',
          data: {},
        });
        break;
      case 'match': {
        const leftItemsById = new Map(
          (quiz.matchData?.leftItems ?? []).map(item => [item.id, item]),
        );
        const rightItemsById = new Map(
          (quiz.matchData?.rightItems ?? []).map(item => [item.id, item]),
        );

        blocks.push({
          type: 'matchAnswer',
          data: {
            answerPairs: (quiz.matchData?.answerPairs ?? []).map(pair => {
              const leftItem = leftItemsById.get(pair.leftId);
              const rightItem = rightItemsById.get(pair.rightId);

              return {
                ...pair,
                leftItem,
                rightItem,
                leftLabel: getMatchItemLabel(leftItem),
                rightLabel: getMatchItemLabel(rightItem),
              };
            }),
          },
        });
        break;
      }
      case 'order': {
        const items = quiz.orderData?.items ?? [];
        const displayNumberById = new Map(
          items.map((item, itemIndex) => [item.id, itemIndex + 1]),
        );

        blocks.push({
          type: 'orderAnswer',
          data: {
            answerNumbers: (quiz.orderData?.correctOrderIds ?? []).flatMap(
              id => displayNumberById.get(id) ?? [],
            ),
          },
        });
        break;
      }
    }
  });

  return blocks;
};
